import React, {ReactElement} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Formik} from 'formik';
import {navigate} from 'gatsby';
import {cx} from '@emotion/css';

import {useLocale} from '../../../RootProvider/LocaleContext';
import {getPageRoute} from '../../../utils/routes/getPageRoute';
import {useSetModalContent} from '../../../RootProvider/ModalContextProvider';
import {Login} from '../../../data/apis/Identity';
import getIdentityError, {IdentityForm} from '../../../data/identity/errorMessages';
import useLogin from '../../../data/identity/useLogin/useLogin';
import {IdentityChallenge} from '../../../types';
import Button, {ButtonVariant} from '../../DesignSystem/Buttons/Button/Button';
import EmailAddressField from '../../DesignSystem/Fields/Email/EmailWithFormikField';
import PasswordField from '../../DesignSystem/Fields/Password/PasswordWithFormikField';
import ForgotPasswordModal from '../ForgotPasswordModal/ForgotPasswordModal';
import redirectByQueryParam from '../../../utils/redirectByQueryParam';
import SinglePageForm from '../../DesignSystem/SinglePageForm/SinglePageForm';
import DynamicLink from '../../DynamicLink/DynamicLink';

import styles from './LoginForm.styles';

interface Props {
  emailAddress?: string;
  redirectUrl?: string;
}

function LoginForm({emailAddress = '', redirectUrl}: Props): ReactElement {
  const {formatMessage} = useIntl();
  const locale = useLocale();
  const showModal: (component: ReactElement) => void = useSetModalContent();
  const {submit, error: identityError, isLoading} = useLogin();

  const redirectQueryParams = redirectUrl ? {r: decodeURIComponent(redirectUrl)} : undefined;

  const initialValues: Login = {
    emailAddress,
    password: '',
  };

  const handleFormikSubmit = async (values: Login) => {
    const successUser = await submit(values);

    if (successUser && successUser.challengeName === IdentityChallenge.NEW_PASSWORD_REQUIRED) {
      navigate(getPageRoute('account.temporaryPassword', locale));
      return;
    }

    if (successUser) {
      redirectByQueryParam(getPageRoute('dashboard.home', locale));
    }
  };

  const isFormDisabled = false;

  return (
    <Formik initialValues={initialValues} onSubmit={handleFormikSubmit} validateOnChange={false} validateOnBlur={false}>
      {({errors, handleSubmit}) => {
        let warningMessage;
        if (identityError) {
          warningMessage = getIdentityError(identityError, formatMessage, IdentityForm.LOGIN);
        }

        return (
          <SinglePageForm
            errors={errors}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            isSubmitLoading={isLoading}
            submitButtonLabel={formatMessage({id: 'phoenix.login.form.button.text'})}
            submitButtonId="login"
            afterSubmitContent={
              <DynamicLink
                className={cx(styles.createAccount)}
                href={getPageRoute('account.create.form', locale, undefined, redirectQueryParams)}
                testId="ui.loginForm.noAccount"
              >
                <FormattedMessage id="phoenix.login.form.noAccount" />
              </DynamicLink>
            }
            isFormDisabled={isFormDisabled}
          >
            <EmailAddressField<Login>
              label={formatMessage({id: 'phoenix.fields.email.label'})}
              name="emailAddress"
              placeholder={formatMessage({id: 'phoenix.fields.email.placeholder'})}
              required
              warningMessage={warningMessage}
              isDisabled={isFormDisabled}
            />
            <PasswordField<Login>
              label={formatMessage({id: 'phoenix.fields.password.label'})}
              name="password"
              required
              warningMessage={warningMessage}
              isDisabled={isFormDisabled}
              skipDefaultValidation
            />
            <div className={cx(styles.forgotPassword)}>
              <Button
                ariaLabel={formatMessage({id: 'phoenix.login.form.forgotPassword'})}
                id="forgotPasswordButton"
                onClick={() => showModal(<ForgotPasswordModal />)}
                type="button"
                variant={ButtonVariant.DEFAULT_TEXT}
                isDisabled={isFormDisabled}
              >
                <FormattedMessage id="phoenix.login.form.forgotPassword" />
              </Button>
            </div>
          </SinglePageForm>
        );
      }}
    </Formik>
  );
}

export default LoginForm;
